import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RouteHistory from "go-core/utils/routeHistory";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { BundleApi } from "../../../../../../../../../../services/Api/Organization/types";
import BundleForm from "../../components/BundleForm/BundleForm";

interface MatchParams {
	bundleId: string;
}

const OrganizationBundleEditPage = () => {
	const { bundleId } = useParams<MatchParams>();
	const [bundle, setBundle] = useState<BundleApi | undefined>();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>();
	const { addFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		init();
	}, []);

	const init = async () => {
		await fetchBundle();
		await fetchResources();
	};

	const fetchBundle = async () => {
		try {
			const params = {
				include:
					"source_stock,destination_stock,custom_fields,items,items.product,items.items,items.items.product,tags,attachments,contact",
			};
			const res = await apiOrganization.getBundle(Number(bundleId), params);
			const parsedRes: BundleApi = {
				...res,
				items: res.items.map((item) => ({
					...item,
					items: [
						...(item.items || []).map((itemItem) => ({
							...itemItem,
							product_id: itemItem.product_id,
							product_name: itemItem.product?.name || null,
							measure_type: itemItem.product?.measure_type || null,
							state: itemItem.product?.state || null,
							unit_fifo_cost_net_money: itemItem.product?.cost_price_money || null,
							quantity: itemItem.quantity,
						})),
					],
				})),
			};
			setBundle(parsedRes);
			handleChangeTabTitle(`${res.document_number} | ${t("modules.bundle.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchResources = async () => {
		try {
			const params = {
				resource_name: "BUNDLE",
				custom_field_resource_type: "BUNDLE",
				custom_field_permission_scope: "WRITE",
			};
			const res = await apiOrganization.getResourceTypes(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!bundle || !customFieldsConfig) return <LoadingContainer />;

	const goBack = () => RouteHistory.goBack(history, `/${organization.id}/documents/bundles/${bundle.id}`);
	const title = `${t("modules.bundle.header.edit.title")} ${bundle.document_number.replace(" ", "-")}`;

	return (
		<>
			<Header title={title} back={goBack} />
			<BundleForm bundle={bundle} customFieldsConfig={customFieldsConfig} />
		</>
	);
};

export default OrganizationBundleEditPage;
